import React, { useState } from "react";
import { Linkedin, Instagram, Send, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await subscribeNewsletter(email);
            toast.success("Subscribed. Welcome to the briefing.");
            setEmail("");
        } catch {
            toast.error("Could not subscribe — please try again.");
        } finally {
            setLoading(false);
        }
    };

    const year = new Date().getFullYear();

    return (
        <footer className="bg-[#0A1547] border-t border-white/5 pt-20 pb-10" data-testid="site-footer">
            <div className="container-x">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
                    {/* Brand */}
                    <div className="md:col-span-5">
                        <div className="mb-6">
                            <Logo height={120} />
                        </div>
                        <p className="text-[#94A3B8] text-sm leading-relaxed max-w-md">
                            Introducing the next generation of traders to a globally regulated,
                            institutional-grade brokerage — one transparent conversation at a time.
                        </p>

                        <form
                            onSubmit={onSubscribe}
                            className="mt-8 flex items-stretch max-w-md border border-[#C8A064]/30"
                            data-testid="newsletter-form"
                        >
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Subscribe to the briefing"
                                className="flex-1 bg-transparent px-4 py-3 text-sm outline-none text-[#F3F4F6] placeholder:text-[#64748B]"
                                required
                                data-testid="newsletter-email-input"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="bg-[#C8A064] text-[#0A1547] px-5 hover:bg-[#E0BE85] transition-colors flex items-center"
                                data-testid="newsletter-submit-btn"
                            >
                                {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                            </button>
                        </form>
                    </div>

                    {/* Columns */}
                    <FooterCol
                        title="Company"
                        items={[
                            { label: "About", to: "/about" },
                            { label: "Team", to: "/about" },
                            { label: "Insights", to: "/insights" },
                            { label: "Contact", to: "/contact" },
                        ]}
                        className="md:col-span-2"
                    />
                    <FooterCol
                        title="Services"
                        items={[
                            { label: "Outreach", to: "/services" },
                            { label: "Education", to: "/services" },
                            { label: "Onboarding", to: "/services" },
                            { label: "IB Program", to: "/contact" },
                        ]}
                        className="md:col-span-2"
                    />
                    <FooterCol
                        title="Vantage"
                        items={[
                            { label: "Why Vantage", to: "/vantage-markets" },
                            { label: "Account Types", to: "/vantage-markets" },
                            { label: "Regulation", to: "/vantage-markets" },
                            { label: "Book a Call", to: "/contact" },
                        ]}
                        className="md:col-span-3"
                    />
                </div>

                <div className="divider-brass mb-8" />

                {/* Bottom row */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div className="text-xs text-[#64748B]">
                        © {year} NexaReach LLC. All rights reserved. ·{" "}
                        <span className="text-[#94A3B8]">Marketing Partner of Vantage Markets</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Social Icon={Linkedin} href="#" testId="social-linkedin" />
                        <Social
                            Icon={Instagram}
                            href="https://www.instagram.com/nexareach.co/"
                            testId="social-instagram"
                        />
                    </div>
                </div>

                {/* Disclaimer */}
                <div className="mt-10 text-[11px] text-[#64748B] leading-relaxed max-w-5xl">
                    <strong className="text-[#94A3B8]">Risk Disclosure:</strong> Trading derivatives
                    (CFDs, forex, indices, commodities, shares, crypto CFDs) carries a high level of
                    risk and may not be suitable for all investors. You may lose more than your
                    invested capital. NexaReach LLC is a marketing partner and introducer of
                    Vantage Markets and does not provide regulated financial advice. Vantage Markets
                    services are subject to local regulations and may not be available in all
                    jurisdictions. Please review Vantage's terms before opening an account.
                </div>
            </div>
        </footer>
    );
}

const FooterCol = ({ title, items, className }) => (
    <div className={className}>
        <div className="overline mb-5">{title}</div>
        <ul className="space-y-3">
            {items.map((it) => (
                <li key={it.label + it.to}>
                    <Link
                        to={it.to}
                        className="text-sm text-[#94A3B8] hover:text-[#C8A064] transition-colors"
                        data-testid={`footer-link-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                    >
                        {it.label}
                    </Link>
                </li>
            ))}
        </ul>
    </div>
);

const Social = ({ Icon, href = "#", testId }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="w-10 h-10 border border-white/10 hover:border-[#C8A064] flex items-center justify-center text-[#94A3B8] hover:text-[#C8A064] transition-colors"
        data-testid={testId}
    >
        <Icon size={14} />
    </a>
);
